import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Stack, Link, Grid } from '@mui/material';
import useEventContext from '@/contexts/event/useEventContext';
import 'dayjs/locale/es-mx';
import Image from '@/components/Image';
import ResumeInfo from '@/components/ResumeInfo';
import useMobile from '../../../../hooks/useMobile';
import {
  formatUTCDate,
  formatDateRange,
  formatLocalDate,
  formatDateRangeShort,
  formatDateRangeFull,
  getHours,
} from '@/locales/dates';
import CustomLinkBanner from '@/sections/custom-links/CustomLinkBanner';
import SaturatedTimer from '../saturated/SaturatedTimer';
import { useSaturated, useFan2Fan } from '@/store';
import useBoletiaConfig from '@/hooks/useBoletiaConfig';
import RenderIf from '@/components/RenderIf';
import Iconify from '@/components/Iconify';
import { openUrl } from '@/utils/miscellaneous';

const GOOGLE_MAPS_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY;

function EventSummary({ onReadMore, isPreRegister = false }) {
  const { t } = useTranslation();
  const [calendarStartDate, setCalendarStartDate] = useState(null);
  const [calendarEndDate, setCalendarEndDate] = useState(null);

  const { setShowFan2Fan } = useFan2Fan();
  const {
    event: {
      event: { name, banner, start_date, end_date, use_seats, id },
      secondary_market,
      location: { venue, latitude, longitude, city, state } = {
        venue: '',
        address: '',
      },
    },
  } = useEventContext();
  const { isMobile } = useMobile();

  const { isEnabled } = useSaturated();

  const showViewMore =
    (use_seats && onReadMore) || (isMobile && !isPreRegister);

  const { isEventWithoutCustomization } = useBoletiaConfig();

  useEffect(() => {
    setCalendarStartDate(formatUTCDate(start_date, 'YYYYMMDDTHHmmss'));

    setCalendarEndDate(formatUTCDate(end_date, 'YYYYMMDDTHHmmss'));
  }, []);

  return (
    <Stack sx={{ gap: 1 }}>
      <Stack spacing={2}>
        <>
          {isMobile ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '20px',
                overflow: 'hidden',
              }}
            >
              <Image
                sx={{ borderRadius: '16px', height: '125px' }}
                src={banner}
                alt="event banner"
                ratio="480/180"
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '16px',
                overflow: 'hidden',
              }}
            >
              <Image src={banner} ratio="480/180" alt="event banner 480" />
            </Box>
          )}
          {/* saturaded logic */}
          {isMobile && isEnabled && <SaturatedTimer disableTries />}
          {isMobile && <CustomLinkBanner />}
        </>
      </Stack>
      <Stack gap={0.5}>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Stack alignItems="center" direction="row">
            <Typography sx={{ fontSize: '20px', fontWeight: 600, lineHeight: '130%' }} component="div">
              {name}
            </Typography>
            <Box sx={{ flex: 1 }}></Box>
            {showViewMore && (
              <Link
                variant="caption"
                sx={{
                  'textDecoration': 'underline',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                  'color': (theme) => theme.palette.secondary.main,
                  'minWidth': '55px',
                }}
                onClick={onReadMore}
              >
                {t('checkout.global.readMore')}
              </Link>
            )}
          </Stack>
        </Box>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Stack spacing={1}>
            <ResumeItem
              icon="ph:calendar-blank-bold"
              text={`${isEventWithoutCustomization(id)
                ? formatDateRangeFull(start_date, end_date)
                : formatLocalDate(start_date)
                } ${isEventWithoutCustomization(id)
                  ? `Inicio: ${getHours(start_date)}`
                  : formatDateRange(start_date, end_date)
                }`}
              url={`http://www.google.com/calendar/render?action=TEMPLATE&text=${name}&dates=${calendarStartDate}/${calendarEndDate}&location=${venue}`}
            />

            {venue !== 'Online' && (
              <ResumeItem
                text={`${venue}, ${city}, ${state}`}
                url={`http://www.google.com/calendar/render?action=TEMPLATE&text=${name}&dates=${calendarStartDate}/${calendarEndDate}&location=${venue}`}
                icon="ph:map-pin-bold"
              />
            )}
            {venue === 'Online' && (
              <ResumeItem
                text={`${venue}`}
                url={`http://www.google.com/calendar/render?action=TEMPLATE&text=${name}&dates=${calendarStartDate}/${calendarEndDate}&location=${venue}`}
                icon="ph:baseline-place"
              />
            )}
            {secondary_market?.has && (
              <ResumeItem
                text="Boletos de Fan a Fan disponibles "
                textIcon="ph:caret-right-bold"
                icon="ph:arrows-clockwise-bold"
                iconColor="#C2185B"
                onClick={() =>
                  setShowFan2Fan({ link: secondary_market?.link, open: true })
                }
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
}

export default EventSummary;

function ResumeItem({
  text,
  icon,
  textIcon,
  iconColor = 'text.secondary',
  url,
  onClick = null,
}) {
  const { isMobile } = useMobile();
  const onAction = () => {
    if (!url) {
      onClick();
      return;
    }
    openUrl(url);
  };
  return (
    <Stack
      direction="row"
      gap="5px"
      alignItems="center"
      onClick={onAction}
      sx={{
        '&:hover': {
          cursor: url || onClick ? 'pointer' : 'normal',
        },
      }}
    >
      <Iconify icon={icon} width={14} height={14} color={iconColor} />
      <Typography
        sx={{ fontSize: isMobile ? '12px' : '14px', lineHeight: '130%' }}
      >
        {text}
      </Typography>
      {textIcon && <Iconify icon={textIcon} width={14} height={14} />}
    </Stack>
  );
}

export { ResumeItem }
